/* growthTypeSwitch.js -- ONE behaviour for the Growth Type switch on BOTH panels that have it:
 * the Misc Revenue line and Company Quota (W-009, Brian 2026-09-23).
 *
 * WHY: a line's monthly values are stored in the unit its growth type names -- DOLLARS under
 * 'flat', PERCENT under 'pct' -- and the select used to change the type without touching the
 * values, so the engine re-read the same numbers in the other unit. Measured
 * (diffs/misc-writer-phase0.txt): flat -> pct on cid=36's real misc line booked about 1.0e22 of
 * revenue; pct -> flat turned 2% into $2. Both values are valid, so no server guard can catch it.
 *
 * THE BEHAVIOUR: switching asks first -- the values are in the other unit and will be cleared --
 * and on OK the type changes and the monthly values are CLEARED; on Cancel nothing changes
 * (the caller keeps its state, so the controlled <select> snaps back).
 *   - "cleared" is an EMPTY MAP ({}), i.e. blank, not zero: both grids render a missing month
 *     as a blank box, and the save sends {} (the Misc PUT and the Company Quota PUT both store
 *     an empty object as '{}' -- neither treats it as "omitted").
 *   - nothing to lose -> no question: when no month holds a non-zero value the switch just
 *     happens (the map is still reset to {}).
 * THE CONFIRMATION IDIOM IS THE CODEBASE'S OWN: window.confirm, as the misc and implementation
 * delete buttons on the same page use it. Resolved at call time, so a test can stand in for it. */

export const GROWTH_TYPE_LABELS = { flat: 'Flat Dollar', pct: 'Percentage' };
const UNIT = { flat: 'a dollar amount', pct: 'a percentage' };

export function hasNonZeroGrowthValue(values) {
  return Object.values(values || {}).some(v => (parseFloat(v) || 0) !== 0);
}

export function growthTypeSwitchMessage(from, to) {
  const f = GROWTH_TYPE_LABELS[from] || from, t = GROWTH_TYPE_LABELS[to] || to;
  return 'Switching Growth Type from "' + f + '" to "' + t + '" changes what every monthly value means: ' +
    'each one is ' + (UNIT[from] || 'a value in the old unit') + ' and would be read as ' + (UNIT[to] || 'a value in the new unit') + '.\n\n' +
    'The monthly values will be cleared so they can be entered in the new unit. Continue?';
}

/* Returns null when nothing should change (same type, or the operator cancelled); otherwise
 * { type, values } -- the new type and the cleared map -- for the caller to set together. */
export function growthTypeSwitch({ from, to, values, confirm }) {
  if (!to || to === from) return null;
  if (hasNonZeroGrowthValue(values)) {
    const ask = confirm || (msg => window.confirm(msg));
    if (!ask(growthTypeSwitchMessage(from, to))) return null;
  }
  return { type: to, values: {} };
}
